import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
  serverTimestamp,
  increment,
  writeBatch
} from 'firebase/firestore';
import logger from '../utils/logger';

/**
 * Messaging Service
 *
 * Handles all Firestore operations for the messaging system:
 * - Conversations (direct, group, family)
 * - Messages (send, edit, soft-delete)
 * - Read receipts (readBy tracking)
 * - Unread counts (per-user, per-conversation)
 *
 * Firestore paths:
 *   families/{familyCode}/conversations/{conversationId}
 *   families/{familyCode}/conversations/{conversationId}/messages/{messageId}
 *   families/{familyCode}/userConversations/{userId}
 */

// ─── Conversations ──────────────────────────────────────────────

/**
 * Subscribe to all conversations the user participates in (real-time).
 * Returns conversations sorted by last message timestamp (newest first).
 */
export const subscribeToConversations = (familyCode, userId, onUpdate) => {
  const conversationsRef = collection(db, 'families', familyCode, 'conversations');
  const q = query(
    conversationsRef,
    where('participants', 'array-contains', userId),
    orderBy('updatedAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const conversations = [];
    snapshot.forEach((doc) => {
      conversations.push({ id: doc.id, ...doc.data() });
    });
    onUpdate(conversations);
  }, (error) => {
    logger.error('Error subscribing to conversations:', error);
    onUpdate([]);
  });
};

/**
 * Create a new conversation.
 * For direct messages, check if one already exists first using findDirectConversation().
 */
export const createConversation = async (familyCode, { type, participants, name, createdBy, createdByName }) => {
  const conversationData = {
    type,
    participants,
    name: name || null,
    createdBy,
    createdByName: createdByName || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessage: null,
    isArchived: false
  };

  const conversationsRef = collection(db, 'families', familyCode, 'conversations');
  const docRef = await addDoc(conversationsRef, conversationData);

  // Initialize userConversations entry for each participant
  for (const participantId of participants) {
    await ensureUserConversationsDoc(familyCode, participantId, docRef.id);
  }

  return docRef.id;
};

/**
 * Find an existing direct conversation between exactly two users.
 * Returns the conversation ID if found, null otherwise.
 */
export const findDirectConversation = async (familyCode, userId1, userId2) => {
  const conversationsRef = collection(db, 'families', familyCode, 'conversations');

  // Query for direct conversations that include userId1
  const q = query(
    conversationsRef,
    where('type', '==', 'direct'),
    where('participants', 'array-contains', userId1)
  );

  const snapshot = await getDocs(q);

  for (const doc of snapshot.docs) {
    const data = doc.data();
    // Check if the other participant is userId2 and there are exactly 2
    if (data.participants.length === 2 && data.participants.includes(userId2)) {
      return doc.id;
    }
  }

  return null;
};

/**
 * Get or create the family-wide chat conversation.
 * Auto-adds all current family members as participants.
 */
export const getOrCreateFamilyChat = async (familyCode, allMemberIds, currentUserId, currentUserName) => {
  const conversationsRef = collection(db, 'families', familyCode, 'conversations');

  // Check if family chat already exists
  const q = query(conversationsRef, where('type', '==', 'family'));
  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    const existingChat = snapshot.docs[0];
    const data = existingChat.data();

    // Add any new members who aren't participants yet
    const newMembers = allMemberIds.filter(id => !data.participants.includes(id));
    if (newMembers.length > 0) {
      await updateDoc(existingChat.ref, {
        participants: [...data.participants, ...newMembers],
        updatedAt: serverTimestamp()
      });
    }

    return existingChat.id;
  }

  // Create new family chat
  return createConversation(familyCode, {
    type: 'family',
    participants: allMemberIds,
    name: 'Family Chat',
    createdBy: currentUserId,
    createdByName: currentUserName
  });
};

/**
 * Get a single conversation by ID.
 */
export const getConversation = async (familyCode, conversationId) => {
  const ref = doc(db, 'families', familyCode, 'conversations', conversationId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

/**
 * Archive or unarchive a conversation for a user.
 */
export const archiveConversation = async (familyCode, conversationId, userId, archive = true) => {
  const userConvRef = doc(db, 'families', familyCode, 'userConversations', userId);
  const snap = await getDoc(userConvRef);

  if (snap.exists()) {
    const convData = snap.data().conversations || {};
    convData[conversationId] = {
      ...(convData[conversationId] || {}),
      isArchived: archive
    };
    await updateDoc(userConvRef, { conversations: convData });
  }
};

// ─── Messages ───────────────────────────────────────────────────

/**
 * Subscribe to messages in a conversation (real-time).
 * Returns messages ordered oldest-first for display.
 */
export const subscribeToMessages = (familyCode, conversationId, onUpdate, messageLimit = 100) => {
  const messagesRef = collection(
    db, 'families', familyCode, 'conversations', conversationId, 'messages'
  );
  const q = query(
    messagesRef,
    orderBy('timestamp', 'asc'),
    firestoreLimit(messageLimit)
  );

  return onSnapshot(q, (snapshot) => {
    const messages = [];
    snapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    onUpdate(messages);
  }, (error) => {
    logger.error('Error subscribing to messages:', error);
    onUpdate([]);
  });
};

/**
 * Send a message in a conversation.
 * Also updates the conversation's lastMessage and updatedAt,
 * and increments unread counts for other participants.
 */
export const sendMessage = async (familyCode, conversationId, { content, authorId, authorName, authorPhoto, type = 'text', imageUrl = null }) => {
  const messageData = {
    content: content || '',
    authorId,
    authorName,
    authorPhoto: authorPhoto || null,
    timestamp: serverTimestamp(),
    type,
    imageUrl: imageUrl || null,
    readBy: { [authorId]: new Date() },
    editedAt: null,
    deletedAt: null
  };

  // Add the message
  const messagesRef = collection(
    db, 'families', familyCode, 'conversations', conversationId, 'messages'
  );
  await addDoc(messagesRef, messageData);

  // Update conversation's lastMessage
  const lastText = type === 'image' ? '📷 Photo' : content.substring(0, 100);
  const conversationRef = doc(db, 'families', familyCode, 'conversations', conversationId);
  await updateDoc(conversationRef, {
    lastMessage: {
      text: lastText,
      authorId,
      authorName,
      timestamp: serverTimestamp()
    },
    updatedAt: serverTimestamp()
  });

  // Increment unread counts for other participants
  const conversationSnap = await getDoc(conversationRef);
  if (conversationSnap.exists()) {
    const participants = conversationSnap.data().participants || [];
    for (const participantId of participants) {
      if (participantId !== authorId) {
        await incrementUnreadCount(familyCode, participantId, conversationId);
      }
    }
  }
};

/**
 * Edit a message (author only). Sets editedAt timestamp.
 */
export const editMessage = async (familyCode, conversationId, messageId, newContent) => {
  const messageRef = doc(
    db, 'families', familyCode, 'conversations', conversationId, 'messages', messageId
  );
  await updateDoc(messageRef, {
    content: newContent,
    editedAt: serverTimestamp()
  });
};

/**
 * Soft-delete a message. Sets deletedAt timestamp.
 * Message remains in DB but displays as "This message was deleted".
 */
export const deleteMessage = async (familyCode, conversationId, messageId) => {
  const messageRef = doc(
    db, 'families', familyCode, 'conversations', conversationId, 'messages', messageId
  );
  await updateDoc(messageRef, {
    deletedAt: serverTimestamp()
  });
};

// ─── Read Receipts & Unread Counts ──────────────────────────────

/**
 * Mark all messages in a conversation as read for the given user.
 * Also resets the user's unread count for this conversation.
 */
export const markAsRead = async (familyCode, conversationId, userId) => {
  // Get unread messages (messages where user isn't in readBy)
  const messagesRef = collection(
    db, 'families', familyCode, 'conversations', conversationId, 'messages'
  );

  // We can't query "where readBy.userId doesn't exist" in Firestore easily,
  // so we fetch recent messages and batch-update ones missing readBy for this user.
  const q = query(messagesRef, orderBy('timestamp', 'desc'), firestoreLimit(50));
  const snapshot = await getDocs(q);

  const batch = writeBatch(db);
  let updatedCount = 0;

  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    if (!data.readBy?.[userId] && data.authorId !== userId) {
      batch.update(docSnap.ref, {
        [`readBy.${userId}`]: new Date()
      });
      updatedCount++;
    }
  });

  if (updatedCount > 0) {
    await batch.commit();
  }

  // Reset unread count for this conversation
  await resetUnreadCount(familyCode, userId, conversationId);
};

/**
 * Subscribe to the user's total unread count (for nav badge).
 */
export const subscribeToUnreadCount = (familyCode, userId, onUpdate) => {
  const userConvRef = doc(db, 'families', familyCode, 'userConversations', userId);

  return onSnapshot(userConvRef, (snap) => {
    if (snap.exists()) {
      onUpdate(snap.data().totalUnread || 0);
    } else {
      onUpdate(0);
    }
  }, (error) => {
    logger.error('Error subscribing to unread count:', error);
    onUpdate(0);
  });
};

/**
 * Get user's per-conversation metadata (unread counts, mute, pin).
 */
export const getUserConversationMeta = async (familyCode, userId) => {
  const ref = doc(db, 'families', familyCode, 'userConversations', userId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return { conversations: {}, totalUnread: 0 };
  return snap.data();
};

// ─── User Preferences ───────────────────────────────────────────

/**
 * Mute/unmute a conversation (suppresses notifications).
 */
export const toggleMute = async (familyCode, conversationId, userId) => {
  const userConvRef = doc(db, 'families', familyCode, 'userConversations', userId);
  const snap = await getDoc(userConvRef);

  if (snap.exists()) {
    const convData = snap.data().conversations || {};
    const current = convData[conversationId] || {};
    convData[conversationId] = { ...current, isMuted: !current.isMuted };
    await updateDoc(userConvRef, { conversations: convData });
  }
};

/**
 * Pin/unpin a conversation to the top of the list.
 */
export const togglePin = async (familyCode, conversationId, userId) => {
  const userConvRef = doc(db, 'families', familyCode, 'userConversations', userId);
  const snap = await getDoc(userConvRef);

  if (snap.exists()) {
    const convData = snap.data().conversations || {};
    const current = convData[conversationId] || {};
    convData[conversationId] = { ...current, isPinned: !current.isPinned };
    await updateDoc(userConvRef, { conversations: convData });
  }
};

// ─── Group Management ────────────────────────────────────────────

/**
 * Add participants to an existing group conversation.
 * Initialises their userConversations entry so it shows in their sidebar.
 */
export const addParticipantsToConversation = async (familyCode, conversationId, newParticipantIds) => {
  const conversationRef = doc(db, 'families', familyCode, 'conversations', conversationId);
  const snap = await getDoc(conversationRef);
  if (!snap.exists()) throw new Error('Conversation not found');

  const data = snap.data();
  const existing = new Set(data.participants || []);
  const toAdd = newParticipantIds.filter(id => !existing.has(id));

  if (toAdd.length === 0) return;

  const updatedParticipants = [...data.participants, ...toAdd];
  await updateDoc(conversationRef, {
    participants: updatedParticipants,
    updatedAt: serverTimestamp()
  });

  // Initialise userConversations for new participants
  for (const id of toAdd) {
    await ensureUserConversationsDoc(familyCode, id, conversationId);
  }
};

/**
 * Update a group conversation's display name.
 */
export const updateConversationName = async (familyCode, conversationId, newName) => {
  const conversationRef = doc(db, 'families', familyCode, 'conversations', conversationId);
  await updateDoc(conversationRef, {
    name: newName,
    updatedAt: serverTimestamp()
  });
};

// ─── Internal Helpers ───────────────────────────────────────────

/**
 * Ensure the userConversations document exists for a user.
 * Uses write-only operations (no getDoc) so it works for other users'
 * docs where only write permission is granted.
 */
const ensureUserConversationsDoc = async (familyCode, userId, conversationId) => {
  const ref = doc(db, 'families', familyCode, 'userConversations', userId);
  try {
    // Try to add the conversation entry via dot-notation (preserves other entries)
    await updateDoc(ref, {
      [`conversations.${conversationId}`]: { unreadCount: 0, isMuted: false, isPinned: false }
    });
  } catch (err) {
    // Document doesn't exist yet — create it
    await setDoc(ref, {
      conversations: {
        [conversationId]: { unreadCount: 0, isMuted: false, isPinned: false }
      },
      totalUnread: 0
    });
  }
};

/**
 * Increment unread count for a user in a specific conversation.
 * Uses atomic increment() — no read required.
 */
const incrementUnreadCount = async (familyCode, userId, conversationId) => {
  const ref = doc(db, 'families', familyCode, 'userConversations', userId);
  try {
    await updateDoc(ref, {
      [`conversations.${conversationId}.unreadCount`]: increment(1),
      totalUnread: increment(1)
    });
  } catch (err) {
    // Document doesn't exist yet — create it with initial count
    await setDoc(ref, {
      conversations: {
        [conversationId]: { unreadCount: 1, isMuted: false, isPinned: false }
      },
      totalUnread: 1
    });
  }
};

/**
 * Reset unread count for a user in a specific conversation.
 */
const resetUnreadCount = async (familyCode, userId, conversationId) => {
  const ref = doc(db, 'families', familyCode, 'userConversations', userId);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    const convData = snap.data().conversations || {};
    const previousUnread = convData[conversationId]?.unreadCount || 0;
    convData[conversationId] = { ...(convData[conversationId] || {}), unreadCount: 0 };

    const currentTotal = snap.data().totalUnread || 0;
    await updateDoc(ref, {
      conversations: convData,
      totalUnread: Math.max(0, currentTotal - previousUnread)
    });
  }
};

/**
 * Get display name for a conversation.
 * Direct: shows the other person's name
 * Group: shows the group name
 * Family: shows "Family Chat"
 */
export const getConversationDisplayName = (conversation, currentUserId, memberMap = {}) => {
  if (conversation.type === 'family') return 'Family Chat';
  if (conversation.type === 'group') return conversation.name || 'Group Chat';

  // Direct: show the other person's name
  if (conversation.type === 'direct') {
    const otherId = conversation.participants?.find(id => id !== currentUserId);
    if (otherId && memberMap[otherId]) {
      return memberMap[otherId].displayName || memberMap[otherId].name || 'Unknown';
    }
    return conversation.name || 'Direct Message';
  }

  return conversation.name || 'Conversation';
};
